import axios from "axios";
import { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { FaCheckCircle, FaDownload, FaEye, FaPencilAlt } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

const AdditionalDetails = () => {
    const BASE_API_URL = process.env.REACT_APP_API_URL;
    const [hrDetails, setHrDetails] = useState(null);
    const [userData, setUserData] = useState({});
    const location = useLocation();

    const userName = location.state?.userName || '';
    const userEmail = location.state?.userEmail || '';
    const [userId, setUserId] = useState()
    const [editMode, setEditMode] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [fileName, setFileName] = useState('');

    const [formData, setFormData] = useState({
        designation: '',
        linkedInLink: '',
        phone: '',
        visitingCard: null,
        visitingCardName: ''
    });

    useEffect(() => {
        if (userEmail) {
            getUser(userEmail);
        }
    }, [userEmail]);

    const getUser = async (userEmail) => {
        try {
            const response = await axios.get(`${BASE_API_URL}/getHRName?userEmail=${userEmail}`);
            setUserData(response.data);
            setUserId(response.data.userId); // <-- Set userId separately
        } catch (error) {
        }
    };
    // Fetch HR details on mount
    useEffect(() => {
        if (userId) {
            axios.get(`${BASE_API_URL}/getHRDetails`, {
                params: { userId }
            })
                .then(res => {
                    setHrDetails(res.data);
                    setFileName(res.data?.visitingCardName || '');
                    setFormData({
                        designation: res.data?.designation || '',
                        linkedInLink: res.data?.linkedInLink || '',
                        phone: res.data?.phone || '',
                        visitingCard: null,
                        visitingCardName: res.data?.visitingCardName || ''
                    });
                })
                .catch(err => {
                    setHrDetails(null);
                });
        }
    }, [userId]);

    const handleEditClick = () => {
        setEditMode(true);
    };

    const handleCancel = () => {
        setEditMode(false);
        setFormData({
            designation: hrDetails?.designation || '',
            linkedInLink: hrDetails?.linkedInLink || '',
            phone: hrDetails?.phone || '',
            visitingCard: null,
            visitingCardName: hrDetails?.visitingCardName || ''
        });
        setFileName(hrDetails?.visitingCardName || '');
    };

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === "visitingCard") {
            const file = files[0];
            if (file) {
                setFormData(prev => ({
                    ...prev,
                    visitingCard: file,
                    visitingCardName: file.name
                }));
                setFileName(file.name);
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSave = async () => {
        const submitData = new FormData();
        submitData.append("userId", userId);
        submitData.append("designation", formData.designation);
        submitData.append("linkedInLink", formData.linkedInLink);
        submitData.append("phone", formData.phone);

        if (formData.visitingCard instanceof File) {
            submitData.append("visitingCard", formData.visitingCard);
            submitData.append("visitingCardName", formData.visitingCardName);
        }

        try {
            const response = await axios.post(`${BASE_API_URL}/updateAdditionalDetails`, submitData, {
                headers: {
                    "Content-Type": "multipart/form-data"
                }
            });

            setHrDetails(prev => ({
                ...prev,
                designation: formData.designation,
                linkedInLink: formData.linkedInLink,
                phone: formData.phone,
                visitingCard: formData.visitingCard instanceof File ?
                    URL.createObjectURL(formData.visitingCard) :
                    prev.visitingCard,
                visitingCardName: formData.visitingCardName || prev.visitingCardName
            }));

            setEditMode(false);
            toast.success("Your details updated successfully");
        } catch (error) {
            toast.error("Failed to update details");
        }
    };

    const handlePreview = () => {
        setShowPreview(true);
    };

    const handleClosePreview = () => {
        setShowPreview(false);
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = `${BASE_API_URL}/getVisitingCard/${userId}`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <>
            <h3>Additional Details <Button onClick={handleEditClick}><FaPencilAlt /></Button></h3>

            <div style={{ border: '1px solid #ccc', padding: 16, borderRadius: 8, marginLeft: '12px', marginRight: '12px' }}>

                {!editMode ? (
                    <div>
                        <p>
                            <strong>Designation:</strong> {hrDetails?.designation || 'Not set'}{" "}
                            {hrDetails?.designation_Verification && (
                                <FaCheckCircle style={{ color: "green" }} title="Verified" />
                            )}
                        </p>
                        <p><strong>LinkedIn:</strong>{' '}
                            {hrDetails?.linkedInLink ? (
                                <a href={hrDetails.linkedInLink} target="_blank" rel="noopener noreferrer">
                                    {hrDetails.linkedInLink}
                                </a>
                            ) : (
                                'Not set'
                            )}
                        </p>

                        <p><strong>Mobile Number:</strong>{hrDetails?.phone || 'Not set'}</p>
                        <p>
                            <strong>Visiting Card:</strong>{' '}
                            {hrDetails && hrDetails.visitingCard ? (
                                <>
                                    {hrDetails.visitingCardName || 'File uploaded'}
                                    <div className="d-flex gap-2 mt-2">
                                        <Button
                                            variant="outline-primary"
                                            onClick={handlePreview}
                                            className="d-flex align-items-center gap-1"
                                        >
                                            <FaEye /> Preview
                                        </Button>
                                        <Button
                                            variant="outline-success"
                                            onClick={handleDownload}
                                            className="d-flex align-items-center gap-1"
                                        >
                                            <FaDownload /> Download
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                'Not uploaded'
                            )}
                        </p>


                    </div>
                ) : (
                    <div style={{ width: '100%' }}>
                        <Form>
                            <Form.Group controlId="formDesignation" className="mb-3">
                                <Form.Label>Designation</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="designation"
                                    value={formData.designation}
                                    onChange={handleChange}
                                    placeholder="Enter your designation"
                                />
                            </Form.Group>
                            <Form.Group controlId="phone" className="mb-3">
                                <Form.Label>Mobile Number</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="Enter your mobile number"
                                />
                            </Form.Group>

                            <Form.Group controlId="formLinkedIn" className="mb-3">
                                <Form.Label>LinkedIn Link</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="linkedInLink"
                                    value={formData.linkedInLink}
                                    onChange={handleChange}
                                    placeholder="Enter LinkedIn profile URL"
                                />
                            </Form.Group>

                            <Form.Group controlId="formVisitingCard" className="mb-3">
                                <Form.Label>Visiting Card (optional)</Form.Label>
                                <Form.Control
                                    type="file"
                                    name="visitingCard"
                                    accept=".jpg,.jpeg,.png,.pdf"
                                    onChange={handleChange}
                                />
                                {formData.visitingCard && (
                                    <p className="text-muted d-block mt-1">
                                        Selected file: {formData.visitingCardName}
                                    </p>
                                )}
                                {!formData.visitingCard && hrDetails?.visitingCardName && (
                                    <p className="text-muted d-block mt-1">
                                        Current file: {hrDetails.visitingCardName}
                                    </p>
                                )}
                            </Form.Group>

                            <div className="d-flex gap-2">
                                <Button variant="primary" onClick={handleSave}>Save</Button>
                                <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
                            </div>
                        </Form>
                    </div>
                )}
            </div>

            {/* Preview Modal */}
            <Modal show={showPreview} onHide={handleClosePreview} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Preview Visiting Card</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    {hrDetails?.visitingCard && (
                        <img
                            src={`${BASE_API_URL}/getVisitingCard/${userId}`}
                            alt="Visiting Card Preview"
                            style={{ maxWidth: '100%', maxHeight: '70vh' }}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `${BASE_API_URL}/getVisitingCard/${userId}`;
                                e.target.style.display = 'none';
                                // If image fails to load, show a message
                                e.target.parentElement.innerHTML =
                                    '<p class="text-muted">Preview not available. Please download the file to view.</p>';
                            }}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClosePreview}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </>
    );
};


export default AdditionalDetails
